import React, { useState, useEffect } from "react";
import { PackageDTO } from "../CategoryService";
import DatePriceModal from "./DatePriceModal";
import { addPackageToCart } from "../../../ShoppingCart/ShoppingCartService";
import { Link } from "react-router-dom";
import { format } from "date-fns";

interface AdventureCardProps {
  packageDTO: PackageDTO;
  onBookNow: (packageId: number) => void;
  fetchCartItems: () => Promise<any[]>;
}

const AdventureCard: React.FC<AdventureCardProps> = ({
  packageDTO,
  onBookNow,
  fetchCartItems,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [unavailableDates, setUnavailableDates] = useState<string[]>([]);
  const mainImage = packageDTO.imageUrls.find((img) => img.isMain) ||
    packageDTO.imageUrls[0] || { url: "default-image-url.jpg" };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-IE", {
      style: "currency",
      currency: "EUR",
    }).format(price);
  };

  const handleBookNow = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleAddToCart = async (
    selectedDate: string,
    selectedPrice: string,
    priceOptionId: number,
    dateRange: { startDate: string; endDate: string }
  ) => {
    try {
      if (packageDTO.id !== undefined) {
        await addPackageToCart(packageDTO.id, priceOptionId, dateRange);
      } else {
        console.error("Package ID is undefined");
      }
      console.log(`Added to cart: ${selectedDate}, ${selectedPrice}`);
  
      setShowModal(false);
  
      const selectedPricingOption = packageDTO.prices.find(option => option.id === priceOptionId);
  
      if (selectedDate.length > 0 && selectedPrice.length > 0 && selectedPricingOption) {
        const commonBookingData = {
          sessionId: localStorage.getItem("sessionId") || "",
          shoppingCartItems: {
            packageId: packageDTO.id,
            packageName: packageDTO.name,
            selectedDate,
            selectedPrice,
            category: packageDTO.categoryName,
            name: packageDTO.name,
            price: packageDTO.priceFrom,
            mainImage: mainImage.url,
            pricingOption: {
              description: selectedPricingOption.description,
              price: selectedPricingOption.price,
              id: selectedPricingOption.id,
              dateRanges: selectedPricingOption.dateRanges,
            },
            shoppingCart: {
              sessionId: localStorage.getItem("sessionId") || "",
            },
          },
        };
        console.log("Common booking data:", commonBookingData);
      }

      fetchCartItems();
    } catch (error) {
      console.error("Error adding to cart:", error);
    }
  };

 

  useEffect(() => {
    const fetchUnavailableDates = async () => {
      const cartItems = await fetchCartItems();
      const dates = cartItems.flatMap((item: any) =>
        item.dateRanges?.map(
          (range: any) =>
            `${format(new Date(range.startDate), 'dd.MM.yyyy')} - ${format(new Date(range.endDate), 'dd.MM.yyyy')}`
        ) || []
      );
      setUnavailableDates(dates);
    };

    fetchUnavailableDates();
  }, [fetchCartItems]);

  // Create dates array with consistent dd.MM.yyyy format for display and comparison
  const dates = packageDTO.prices.flatMap((price) =>
    price.dateRanges.map((range) => 
      `${format(new Date(range.startDate), 'dd.MM.yyyy')} - ${format(new Date(range.endDate), 'dd.MM.yyyy')}`
    )
  );

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Filter dates to only show future dates - parse using consistent format
  const futureDates = dates.filter(dateStr => {
    // Parse the start date from "dd.MM.yyyy - dd.MM.yyyy" format
    const startDateParts = dateStr.split(' - ')[0].split('.');
    const startDate = new Date(
      parseInt(startDateParts[2]), // year
      parseInt(startDateParts[1]) - 1, // month (0-indexed)
      parseInt(startDateParts[0]) // day
    );
    return startDate >= today;
  });

  const priceOptions = packageDTO.prices.map((option) => ({
    description: option.description,
    price: option.price,
    id: option.id,
    dateRanges: option.dateRanges,
  }));

  return (
    <div className="adventure-card">
      <div className="adventure-card__image">
        <img src={mainImage.url} alt={packageDTO.name} />
      </div>
      <div className="adventure-card__content">
        <h4 className="adventure-card__content__category">
          {packageDTO.categoryName}
        </h4>
        <h3 className="adventure-card__content__title">{packageDTO.name}</h3>
        <div className="adventure-card__content__description">
          {packageDTO.description}&nbsp;
          {packageDTO.packageUrl && (
            <Link to={packageDTO.packageUrl} target="_blank" rel="noopener noreferrer">
            details
            </Link>
          )}
        </div>
        <h4 className="adventure-card__content__price">
          Price From: {formatPrice(packageDTO.priceFrom)} (VAT included net business customer -20%)
        </h4>
        <button
          className="adventure-card__content__button"
          onClick={handleBookNow}
        >
          Book Now
        </button>
      </div>
      {showModal && (
        <DatePriceModal
          dates={futureDates}
          priceOptions={priceOptions}
          onClose={handleCloseModal}
          onAddToCart={handleAddToCart}
          unavailableDates={unavailableDates} // Pass unavailableDates to DatePriceModal
          packageId={packageDTO.id ?? 0} // Pass packageId to DatePriceModal
        />
      )}
    </div>
  );
};

export default AdventureCard;
